import { Header } from "@/components/Header";
import { useSEO } from "@/hooks/use-seo";
import { Footer } from "@/components/Footer";
import {
  Shield, Users, BookOpen, TrendingUp, CheckCircle, Star,
  Mail, Eye, Award, Heart, MessageCircle, Clock, FileText,
  Zap, Target, Globe, Calendar, ArrowRight
} from "lucide-react";

const VALUES = [
  {
    icon: Eye,
    title: "Transparentnost",
    desc: "Vždy uvádíme pravdu o bonusech, bez skrytých triků. Otevřeně informujeme o affiliate spolupráci.",
  },
  {
    icon: Award,
    title: "Odbornost",
    desc: "Opíráme se o více než 30 let kombinovaných zkušeností v oboru.",
  },
  {
    icon: Users,
    title: "Komunita",
    desc: "Jsme hráči stejně jako vy, proto víme, co hledáte. Píšeme pro reálného hráče.",
  },
  {
    icon: Heart,
    title: "Zodpovědnost",
    desc: "Hazard má být především zábava. Doporučujeme pouze legálně licencovaná kasina.",
  },
];

const STATS = [
  { value: "30+", label: "let zkušeností", icon: Award },
  { value: "20+", label: "otestovaných kasin", icon: CheckCircle },
  { value: "100+", label: "publikovaných recenzí", icon: FileText },
  { value: "24/7", label: "aktualizace obsahu", icon: Clock },
];

const MISSION_HIGHLIGHTS = [
  { icon: CheckCircle, text: "Nezávislé recenze licencovaných kasin s ověřenými daty" },
  { icon: TrendingUp, text: "Porovnání aktuálních bonusových nabídek na českém trhu" },
  { icon: Shield, text: "Praktické tipy, jak hrát zodpovědně a bezpečně" },
  { icon: Globe, text: "Pouze kasina s povolením Ministerstva financí ČR" },
];

const VISION_ITEMS = [
  "Videorecenze a praktické návody krok za krokem",
  "Poradna hráčů s tipy pro zodpovědné hraní",
  "Diskusní fórum pro sdílení zkušeností hráčů",
  "Rozšíření databáze recenzí o nové operátory na českém trhu",
];

const PARTNERS = [
  { name: "GamCare", category: "Zodpovědné hraní" },
  { name: "MGA", category: "Licenční autorita" },
  { name: "BeGambleAware", category: "Prevence" },
  { name: "eCOGRA", category: "Certifikace" },
];

export default function ONasPage() {
  useSEO({ title: "O nás – Nezávislý tým expertů na online kasina | HrajBonus", description: "Poznejte tým HrajBonus – nezávislých expertů na online kasina. Transparentní hodnocení bez reklam. Věříme v zodpovědné hraní. 18+",
  url: "https://hrajbonus.com/o-nas", });

  return (
    <div className="min-h-screen bg-[#f6f8fc]" style={{ fontFamily: "Poppins, sans-serif" }}>
      <Header />

      <section
        className="pt-28 pb-16 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #001a4d 0%, #002d7c 50%, #003da5 100%)" }}
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-10"
            style={{ background: "radial-gradient(circle, #4d88ff 0%, transparent 70%)", transform: "translate(30%, -40%)" }} />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10"
            style={{ background: "radial-gradient(circle, #003da5 0%, transparent 70%)", transform: "translate(-30%, 40%)" }} />
        </div>

        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6">
              <Users className="w-3.5 h-3.5 text-white/70" />
              <span className="text-white/80 text-xs font-semibold uppercase tracking-wider">O nás</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight mb-5">
              Komunita hráčů<br />a odborníků
            </h1>
            <p className="text-white/80 text-lg leading-relaxed max-w-2xl">
              Jsme tým s více než 30 lety kombinovaných zkušeností v online hazardu. Naším cílem je poskytnout českým hráčům objektivní, ověřené a srozumitelné informace, aby se mohli rozhodovat dobře a bezpečně.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5" data-testid="badge-updated-about">
              <Calendar className="w-3.5 h-3.5 text-white/70" />
              <span className="text-white/80 text-xs font-semibold">Aktualizováno: 12. března 2026</span>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border-b border-[#e8edf5]">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-[#e8edf5]">
            {STATS.map(({ value, label, icon: Icon }) => (
              <div key={label} className="flex flex-col items-center justify-center py-8 px-4 gap-2" data-testid={`stat-${label.replace(/\s+/g, '-')}`}>
                <Icon className="w-5 h-5 text-[#003da5] mb-1" />
                <span className="text-3xl font-black text-[#003da5]">{value}</span>
                <span className="text-gray-500 text-sm font-medium text-center">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 py-16 space-y-20">

        <section>
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="inline-flex items-center gap-1.5 text-[#003da5] text-xs font-bold uppercase tracking-widest mb-3">
                <Target className="w-3.5 h-3.5" /> Naše poslání
              </span>
              <h2 className="text-3xl font-black text-gray-900 mb-5 leading-tight">
                Férové informace od&nbsp;hráčů pro hráče
              </h2>
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                {MISSION_HIGHLIGHTS.map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-start gap-3 p-4 bg-[#f3f6fb] rounded-xl border border-[#dde4ee]">
                    <Icon className="w-5 h-5 text-[#003da5] mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 text-sm leading-relaxed">{text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-5">
              <p className="text-gray-600 leading-relaxed">
                Hazardní průmysl je obrovský a dynamicky se vyvíjí. Pro běžného hráče není vždy snadné rozpoznat, které online kasino je spolehlivé, kde jsou bonusy skutečně výhodné a jaká pravidla se ve skutečnosti skrývají v podmínkách.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Za naším projektem stojí lidé, kteří mají dohromady více než 30 let zkušeností u předních operátorů na českém trhu. Víme nejen to, co vidí hráč na obrazovce, ale i to, co se odehrává na pozadí.
              </p>
            </div>
          </div>
        </section>

        <section>
          <div className="bg-gradient-to-br from-[#001a4d] to-[#003da5] rounded-3xl p-8 sm:p-10" data-testid="callout-how-we-rate">
            <div className="grid lg:grid-cols-5 gap-8 items-center">
              <div className="lg:col-span-3">
                <span className="inline-flex items-center gap-1.5 text-white/60 text-xs font-bold uppercase tracking-widest mb-3">
                  <BookOpen className="w-3.5 h-3.5" /> Jak hodnotíme
                </span>
                <h2 className="text-2xl font-black text-white mb-4">Každé kasino prochází 8krokovým testováním</h2>
                <p className="text-white/70 leading-relaxed mb-5">
                  Od registrace a KYC procesu, přes analýzu bonusů a platebních metod, až po kontrolu licence a nástrojů pro zodpovědné hraní — každé kasino hodnotíme podle standardizovaného procesu.
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {["Registrace & KYC", "Bonusy", "Platby", "Bezpečnost", "Zodpovědné hraní", "Herní nabídka", "Podpora", "Publikace"].map((tag) => (
                    <span key={tag} className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-white/10 text-white/80 border border-white/15">
                      {tag}
                    </span>
                  ))}
                </div>
                <a
                  href="/redakcni-metodologie"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-[#003da5] bg-white hover:bg-white/90 transition-all"
                  data-testid="link-how-we-rate"
                >
                  Celá metodologie <ArrowRight className="w-4 h-4" />
                </a>
              </div>
              <div className="lg:col-span-2 grid grid-cols-2 gap-3">
                {[
                  { n: "01", label: "Registrace" },
                  { n: "02", label: "Bonusy" },
                  { n: "03", label: "Platby" },
                  { n: "04", label: "Bezpečnost" },
                  { n: "05", label: "Zodpovědnost" },
                  { n: "06", label: "Hry" },
                  { n: "07", label: "Podpora" },
                  { n: "08", label: "Publikace" },
                ].map((s) => (
                  <div key={s.n} className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 flex items-center gap-2.5">
                    <span className="w-7 h-7 rounded-lg bg-white/15 flex items-center justify-center text-white text-[10px] font-black flex-shrink-0">
                      {s.n}
                    </span>
                    <span className="text-white/80 text-xs font-semibold">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section
          className="rounded-3xl overflow-hidden"
          style={{ background: "linear-gradient(135deg, #001a4d 0%, #002d7c 50%, #003da5 100%)" }}
        >
          <div className="p-8 sm:p-12 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-1.5 text-white/60 text-xs font-bold uppercase tracking-widest mb-4">
                <Globe className="w-3.5 h-3.5" /> Proč právě český trh
              </span>
              <h2 className="text-3xl font-black text-white leading-tight mb-5">
                Zaměřeni výhradně na Českou republiku
              </h2>
              <p className="text-white/75 leading-relaxed mb-5">
                Existuje mnoho mezinárodních webů recenzujících kasina po celém světě, ale my věříme, že hráči v České republice potřebují vlastní platformu, která bere v úvahu lokální licenci, české zákony a potřeby domácí komunity.
              </p>
              <p className="text-white/75 leading-relaxed">
                Na hrajbonus.com se nesetkáte s doporučeními na kasina, která u nás legálně nepůsobí. Doporučujeme pouze ty operátory, kteří mají povolení{" "}
                <a href="https://www.mfcr.cz/" target="_blank" rel="noopener noreferrer" className="underline text-white/90 hover:text-white" data-testid="link-mfcr">Ministerstva financí ČR</a>.
              </p>
            </div>
            <div className="space-y-3">
              {[
                "Recenzujeme výhradně kasina s českou nebo relevantní licencí",
                "Všechny bonusy uvádíme v Kč, bez konverzí a překvapení",
                "Podmínky interpretujeme dle českého právního prostředí",
                "Zákaznická podpora v češtině je pro nás hodnotícím kritériem",
                "Spolupracujeme s organizacemi pro zodpovědné hraní v ČR",
              ].map(item => (
                <div key={item} className="flex items-start gap-3 bg-white/10 rounded-xl px-4 py-3">
                  <CheckCircle className="w-4 h-4 text-white/60 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80 text-sm leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section>
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-1.5 text-[#003da5] text-xs font-bold uppercase tracking-widest mb-3">
              <Star className="w-3.5 h-3.5" /> Naše hodnoty
            </span>
            <h2 className="text-3xl font-black text-gray-900 leading-tight">
              Značka, které můžete věřit
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl border border-[#dde4ee] p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
                <div className="w-12 h-12 rounded-xl bg-[#eef3ff] flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-[#003da5]" />
                </div>
                <h3 className="font-black text-gray-900 text-base mb-2">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-1.5 text-[#003da5] text-xs font-bold uppercase tracking-widest mb-3">
              <Award className="w-3.5 h-3.5" /> Uznáváni a podporováni
            </span>
            <h2 className="text-3xl font-black text-gray-900 leading-tight">
              Naši partneři a reference
            </h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto">
              Spolupracujeme s předními organizacemi v oblasti zodpovědného hraní a licenčních autorit.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="section-partners">
            {PARTNERS.map((partner) => (
              <div
                key={partner.name}
                className="bg-white rounded-2xl border-2 border-dashed border-[#dde4ee] p-6 flex flex-col items-center justify-center text-center min-h-[140px] hover:border-[#003da5]/30 transition-colors"
                data-testid={`partner-frame-${partner.name.toLowerCase()}`}
              >
                <div className="w-16 h-16 rounded-2xl bg-[#f3f6fb] border border-[#dde4ee] flex items-center justify-center mb-3">
                  <span className="text-[#003da5] text-sm font-black">{partner.name.substring(0, 3).toUpperCase()}</span>
                </div>
                <span className="text-gray-900 text-sm font-bold">{partner.name}</span>
                <span className="text-gray-400 text-xs mt-0.5">{partner.category}</span>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="inline-flex items-center gap-1.5 text-[#003da5] text-xs font-bold uppercase tracking-widest mb-3">
                <Zap className="w-3.5 h-3.5" /> Naše vize
              </span>
              <h2 className="text-3xl font-black text-gray-900 leading-tight mb-5">
                Nejdůvěryhodnější zdroj o online kasinech v ČR
              </h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                Chceme, aby každý hráč, který hledá bonus, novou herní platformu nebo radu, věděl, že na hrajbonus.com najde férové a ověřené odpovědi.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Do budoucna plánujeme rozšiřovat náš obsah tak, aby hráči měli k dispozici ještě více nástrojů pro informovaná rozhodnutí.
              </p>
            </div>
            <div className="space-y-3">
              {VISION_ITEMS.map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-[#f3f6fb] rounded-xl border border-[#dde4ee]">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[10px] font-black"
                    style={{ background: "linear-gradient(135deg, #003da5, #002d7c)" }}
                  >
                    {i + 1}
                  </div>
                  <span className="text-gray-700 text-sm leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <a
              href="/nasi-autori"
              className="group bg-white rounded-2xl border border-[#dde4ee] shadow-sm p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex items-start gap-4"
              data-testid="cta-authors"
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #003da5, #002d7c)" }}>
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1 group-hover:text-[#003da5] transition-colors">Poznejte náš tým</h3>
                <p className="text-sm text-gray-500 leading-relaxed">Kdo stojí za každou recenzí — naši autoři a jejich odbornost.</p>
              </div>
            </a>
            <a
              href="/redakcni-metodologie"
              className="group bg-white rounded-2xl border border-[#dde4ee] shadow-sm p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex items-start gap-4"
              data-testid="cta-methodology"
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #003da5, #002d7c)" }}>
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1 group-hover:text-[#003da5] transition-colors">Naše metodologie</h3>
                <p className="text-sm text-gray-500 leading-relaxed">Zjistěte, jak vzniká každá recenze kasina krok za krokem.</p>
              </div>
            </a>
            <a
              href="mailto:info@aroundcasinos.com"
              className="group bg-white rounded-2xl border border-[#dde4ee] shadow-sm p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex items-start gap-4 sm:col-span-2 lg:col-span-1"
              data-testid="cta-contact"
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #003da5, #002d7c)" }}>
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-1 group-hover:text-[#003da5] transition-colors">Kontaktujte nás</h3>
                <p className="text-sm text-gray-500 leading-relaxed">Máte otázky nebo návrhy? Ozvěte se nám.</p>
              </div>
            </a>
          </div>
        </section>

        <section>
          <div className="bg-white rounded-3xl border border-[#dde4ee] shadow-sm p-8 sm:p-12">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="inline-flex items-center gap-1.5 text-[#003da5] text-xs font-bold uppercase tracking-widest mb-3">
                  <MessageCircle className="w-3.5 h-3.5" /> Kontaktujte nás
                </span>
                <h2 className="text-3xl font-black text-gray-900 leading-tight mb-4">
                  Rádi uslyšíme váš názor
                </h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Pokud máte dotazy, tipy nebo návrhy na zlepšení, budeme rádi, když se nám ozvete. Věříme, že jen díky zpětné vazbě od hráčů můžeme náš projekt posouvat dál.
                </p>
                <a
                  href="mailto:info@aroundcasinos.com"
                  className="inline-flex items-center gap-2.5 px-6 py-3 rounded-xl font-bold text-white transition-all hover:opacity-90"
                  style={{ background: "linear-gradient(135deg, #003da5, #002d7c)", boxShadow: "0 4px 14px rgba(0,61,165,0.30)" }}
                  data-testid="link-contact-email"
                >
                  <Mail className="w-4 h-4" />
                  info@aroundcasinos.com
                </a>
              </div>

              <div className="space-y-3">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="font-bold text-amber-800 text-sm mb-1">Upozornění na affiliate obsah</div>
                  <p className="text-amber-700 text-xs leading-relaxed">
                    Některé odkazy na našem webu jsou affiliate odkazy. Pokud vložíte peníze do kasina přes náš odkaz, můžeme obdržet provizi. To však žádným způsobem neovlivňuje podmínky bonusů ani naše hodnocení.
                  </p>
                </div>
                <div className="p-4 bg-green-50 border border-green-100 rounded-xl">
                  <div className="font-bold text-green-800 text-sm mb-1">Zodpovědné hraní</div>
                  <p className="text-green-700 text-xs leading-relaxed">
                    Hazardní hry jsou určeny pro zábavu. Vždy hrajte v rámci svých možností a sázejte jen tolik, kolik si můžete dovolit prohrát. Potřebujete-li pomoc, kontaktujte organizaci <strong>GamCare</strong> nebo jinou specializovanou podporu.
                  </p>
                </div>
                <div className="p-4 bg-[#f3f6fb] border border-[#dde4ee] rounded-xl">
                  <p className="text-gray-500 text-xs leading-relaxed">
                    Třetí strany si vyhrazují právo kdykoli bez předchozího upozornění bonusy a promo akce změnit nebo zrušit. Společnost hrajbonus.com proto nenese odpovědnost za případné nepřesné informace.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
